import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'access-guide');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const category = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91n.con/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://www.91n.con/category/${activeCategory}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${category?.name || '分类'} - 91n.con 官方入口导航`,
        description: category?.description || '91n.con 内容分类页面',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />
      <ScrollToTop />

      <div className="pt-16">
        <Breadcrumb items={[{ label: category?.name || '分类' }]} />
      </div>

      <main className="py-8 md:py-12 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-64 flex-shrink-0">
                <div className="bg-background-100 rounded-xl p-4 border border-background-200/70 lg:sticky lg:top-24">
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-3 px-2">
                    内容分类
                  </h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => setActiveCategory(cat.slug)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                          activeCategory === cat.slug
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/50'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span className="whitespace-nowrap">{cat.name}</span>
                        <span className={`ml-auto text-xs ${activeCategory === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                          {cat.count}
                        </span>
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    {category?.name || '分类'}
                  </h1>
                  <p className="text-foreground-500 text-sm">
                    {category?.description || '浏览相关内容'}
                  </p>
                </div>

                {categoryArticles.length > 0 ? (
                  <div className="space-y-4">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="block bg-background-100 hover:bg-background-200/50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer group"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                          <h2 className="font-heading font-semibold text-foreground-900 text-base group-hover:text-primary-500 transition-colors">
                            {article.title}
                          </h2>
                          <div className="flex items-center gap-3 text-xs text-foreground-400 flex-shrink-0">
                            <span className="flex items-center gap-1">
                              <i className="ri-time-line"></i>
                              {article.readTime}
                            </span>
                            <span>{article.publishedAt}</span>
                          </div>
                        </div>
                        <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          <span className="text-primary-500 text-xs font-medium ml-auto flex items-center gap-1">
                            阅读全文
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                      <i className="ri-inbox-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}